import { useLayoutEffect, useRef, useState } from "react";

/*
 * Segmented control, ported from the Tixora template's HeroUI tabs (primary
 * variant). For one choice among 2 to 4 equal options that changes what you enter or see;
 * filtering a list stays with filter buttons. A radio group: arrow keys move the choice, the
 * thumb slides under it. Styles in styles/controls.css.
 */
type Option<T extends string> = { id: T; label: string; disabled?: boolean };

type SegmentedProps<T extends string> = {
    className?: string;
    /** Accessible name of the group. */
    label: string;
    options: ReadonlyArray<Option<T>>;
    value: T;
    onChange: (value: T) => void;
    size?: "sm" | "md";
    fullWidth?: boolean;
    /** The whole group, while a change is being saved or the choice is not the person's to make. */
    disabled?: boolean;
};

const Segmented = <T extends string>({ className, label, options, value, onChange, size = "md", fullWidth, disabled }: SegmentedProps<T>) => {
    const track = useRef<HTMLDivElement>(null);
    const [thumb, setThumb] = useState<{ left: number; width: number } | null>(null);

    useLayoutEffect(() => {
        const element = track.current;
        if (!element) return;
        const measure = () => {
            const chosen = element.querySelector<HTMLButtonElement>('[aria-checked="true"]');
            setThumb(chosen ? { left: chosen.offsetLeft, width: chosen.offsetWidth } : null);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
    }, [value, options]);

    const move = (from: number, step: number) => {
        if (disabled) return;
        const enabled = options.map((option, index) => ({ option, index })).filter(({ option }) => !option.disabled);
        const position = enabled.findIndex(({ index }) => index === from);
        const next = enabled[(position + step + enabled.length) % enabled.length];
        if (!next) return;
        onChange(next.option.id);
        track.current?.querySelectorAll<HTMLButtonElement>(".segmented__option")[next.index]?.focus();
    };

    return (
        <div
            ref={track}
            role="radiogroup"
            aria-label={label}
            aria-disabled={disabled || undefined}
            className={`segmented ${size === "sm" ? "segmented--sm" : ""} ${fullWidth ? "segmented--full-width" : ""} ${className || ""}`}
        >
            {thumb ? <span className="segmented__thumb" aria-hidden="true" style={{ transform: `translateX(${thumb.left}px)`, width: thumb.width }} /> : null}
            {options.map((option, index) => {
                const checked = option.id === value;
                return (
                    <button
                        type="button"
                        role="radio"
                        aria-checked={checked}
                        tabIndex={checked ? 0 : -1}
                        disabled={disabled || option.disabled}
                        className="segmented__option"
                        key={option.id}
                        onClick={() => { if (!disabled) onChange(option.id); }}
                        onKeyDown={(event) => {
                            // Left and right follow the reading direction: in a right to left page, Right moves back.
                            const forward = getComputedStyle(event.currentTarget).direction === "rtl" ? "ArrowLeft" : "ArrowRight";
                            const backward = forward === "ArrowLeft" ? "ArrowRight" : "ArrowLeft";
                            if (event.key === forward || event.key === "ArrowDown") { event.preventDefault(); move(index, 1); }
                            if (event.key === backward || event.key === "ArrowUp") { event.preventDefault(); move(index, -1); }
                        }}
                    >
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
};

export default Segmented;
